import { FC, useEffect, useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import classNames from 'classnames';

import { setActiveTool } from '../../../store/meetActiveTool.slice';
import { PsyToolsId } from '../../meet/tools-panel/types';
import { useAppDispatch } from '../../../hooks/redux';

import { ColorCards } from './color-cards';
import { IColorCodeAnalysisResponse, useSendColorCodeAnalysisMutation } from '../../../services/colorCode.api';
import { CountdownTimer } from './countdown-timer';
import { IJournalResult } from '../../../store/types';

interface IColorCodePsyProps {
  result?: IJournalResult;
  setResult?: React.Dispatch<React.SetStateAction<IJournalResult>>;
  onToolActivated?: (tool: string) => void;
  onActiveTabChange?: (hasActiveTab: boolean) => void;
  sendColorCodeOpen?: () => void;
  clientColorInbound?: { nonce: number; selectedColors: string[] } | null;
}

const COLORS = ['#D90018', '#42AA02', '#93532C', '#FFCD0B', '#51007E', '#013C7E', '#817F85', '#010001'];
const COLOR_NAME_BY_HEX: Record<string, string> = {
  '#D90018': 'RED',
  '#42AA02': 'GREEN',
  '#93532C': 'BROWN',
  '#FFCD0B': 'YELLOW',
  '#51007E': 'PURPLE',
  '#013C7E': 'BLUE',
  '#817F85': 'GRAY',
  '#010001': 'BLACK',
};

type AnalysisTabKey = 'diagnostics' | 'techniques' | 'hintQuestions';
type SliderTabKey = Exclude<AnalysisTabKey, 'diagnostics'>;
const COUNTDOWN_SECONDS = 30;
const COUNTDOWN_DURATION_MS = COUNTDOWN_SECONDS * 1000;

const ANALYSIS_TABS: { key: AnalysisTabKey; label: string }[] = [
  { key: 'diagnostics', label: 'Заключение' },
  { key: 'hintQuestions', label: 'Вопросы-подсказки' },
  { key: 'techniques', label: 'Техники' },
];

export const ColorCodePsy: FC<IColorCodePsyProps> = ({
  result,
  setResult,
  onToolActivated,
  onActiveTabChange,
  sendColorCodeOpen,
  clientColorInbound,
}) => {
  const dispatch = useAppDispatch();
  const [sendColorCodeAnalysis] = useSendColorCodeAnalysisMutation();
  const [active, setActive] = useState(false);
  const [clientColors, setClientColors] = useState<string[] | null>(null);
  const [activeTab, setActiveTab] = useState<AnalysisTabKey | null>(null);
  const [analysisData, setAnalysisData] = useState<IColorCodeAnalysisResponse | null>(null);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(false);
  const [remainingMs, setRemainingMs] = useState(COUNTDOWN_DURATION_MS);
  const [isCountdownExpired, setIsCountdownExpired] = useState(false);
  const [sliderIndexes, setSliderIndexes] = useState<Record<SliderTabKey, number>>({
    techniques: 0,
    hintQuestions: 0,
  });

  const isProcessingRef = useRef(false);
  const lastClientColorsRef = useRef<string | null>(null);
  const countdownFrameRef = useRef<number | null>(null);
  const countdownStartRef = useRef<number | null>(null);

  useEffect(() => {
    if (!clientColors?.length) {
      isProcessingRef.current = false;
      lastClientColorsRef.current = null;
      setAnalysisData(null);
      return;
    }

    // Создаем уникальный ключ из цветов (с учетом порядка, так как порядок важен для анализа)
    const colorsKey = JSON.stringify(clientColors);

    // Если это тот же самый массив цветов (не изменился), и мы уже обрабатываем - не отправляем запрос
    if (lastClientColorsRef.current === colorsKey && isProcessingRef.current) {
      return;
    }

    // Если цвета изменились - это новый выбор клиента, всегда отправляем новый запрос
    // Сбрасываем флаги и начинаем обработку
    lastClientColorsRef.current = colorsKey;
    isProcessingRef.current = true;
    let isMounted = true;

    const sendAnalysis = async () => {
      setIsAnalysisLoading(true);
      setAnalysisError(null);
      setAnalysisData(null);
      setActiveTab('diagnostics');
      setSliderIndexes({ techniques: 0, hintQuestions: 0 });

      try {
        const normalizedColors = clientColors.map((color) => {
          const key = color.toUpperCase();

          return COLOR_NAME_BY_HEX[key] ?? key;
        });
        const analysisResult = await sendColorCodeAnalysis({ color_cards: normalizedColors }).unwrap();
        if (isMounted) {
          setAnalysisData(analysisResult);

          // Сохраняем анализ в result
          if (setResult) {
            setResult((prev) => {
              const colorCodeArray = Array.isArray(prev.color_code) ? prev.color_code : [];
              const updatedColorCode = [...colorCodeArray];
              // Находим последнюю запись с такими же цветами
              const lastIndex = updatedColorCode.length - 1;
              if (lastIndex >= 0) {
                const lastEntry = updatedColorCode[lastIndex];
                // Проверяем, что цвета совпадают
                const colorsMatch =
                  lastEntry.colors.length === clientColors.length &&
                  lastEntry.colors.every((color, idx) => color === clientColors[idx]);

                if (colorsMatch) {
                  updatedColorCode[lastIndex] = {
                    ...lastEntry,
                    analysis: {
                      diagnostics: analysisResult.diagnostics,
                      techniques: analysisResult.techniques,
                      hintQuestions: analysisResult.hintQuestions,
                    },
                  };
                }
              }

              return {
                ...prev,
                color_code: updatedColorCode,
              };
            });
          }
        }
      } catch (error) {
        if (isMounted) {
          setAnalysisError('Не удалось получить анализ. Попробуйте ещё раз.');
        }
        console.error('Failed to send color-code analysis request', error);
      } finally {
        if (isMounted) {
          setIsAnalysisLoading(false);
          isProcessingRef.current = false;
        }
      }
    };

    sendAnalysis();

    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientColors]);

  useEffect(() => {
    const durationMs = COUNTDOWN_DURATION_MS;

    const stopCountdown = () => {
      if (countdownFrameRef.current !== null) {
        cancelAnimationFrame(countdownFrameRef.current);
        countdownFrameRef.current = null;
      }
      countdownStartRef.current = null;
    };

    const startCountdown = () => {
      stopCountdown();
      setRemainingMs(durationMs);
      setIsCountdownExpired(false);
      const tick = (timestamp: number) => {
        if (countdownStartRef.current === null) {
          countdownStartRef.current = timestamp;
        }
        const elapsed = timestamp - countdownStartRef.current;
        const remaining = Math.max(0, durationMs - elapsed);
        setRemainingMs(remaining);
        if (remaining > 0 && isAnalysisLoading) {
          countdownFrameRef.current = requestAnimationFrame(tick);
        } else {
          stopCountdown();
          setIsCountdownExpired(remaining <= 0);
        }
      };

      countdownFrameRef.current = requestAnimationFrame(tick);
    };

    if (isAnalysisLoading) {
      startCountdown();
    } else {
      stopCountdown();
      setRemainingMs(durationMs);
      setIsCountdownExpired(false);
    }

    return () => {
      stopCountdown();
    };
  }, [isAnalysisLoading]);

  const handleStartTest = () => {
    sendColorCodeOpen?.();
    dispatch(setActiveTool(PsyToolsId.COLOR_CODE));
    onToolActivated?.('color_code');
    setActive(true);
  };

  useEffect(() => {
    if (!clientColorInbound?.selectedColors?.length) {
      return;
    }

    const selectedColors = clientColorInbound.selectedColors;
    setClientColors(selectedColors);

    if (selectedColors.length > 0 && setResult) {
      const data = {
        date_time: new Date().toISOString(),
        colors: selectedColors,
        comment: '',
      };

      setResult((prev) => ({
        ...prev,
        color_code: Array.isArray(prev.color_code) ? [...prev.color_code, data] : [data],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- обрабатываем только новую посылку по nonce
  }, [clientColorInbound?.nonce]);

  const handleTabClick = (tabKey: AnalysisTabKey) => {
    setActiveTab(tabKey);
  };

  useEffect(() => {
    onActiveTabChange?.(activeTab !== null);
  }, [activeTab, onActiveTabChange]);

  const renderAnalysisContent = () => {
    if (!activeTab) return null;

    if (isAnalysisLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-full text-center gap-4">
          <CountdownTimer remainingMs={remainingMs} isExpired={isCountdownExpired} durationMs={COUNTDOWN_DURATION_MS} />
          <p className="text-[#7A7A85] text-base font-medium">ИИ анализирует выбранные карты…</p>
          {isCountdownExpired && <p className="text-[#C00000] text-sm font-semibold">Подождите еще чуть-чуть</p>}
        </div>
      );
    }

    if (analysisError) {
      return (
        <div className="flex items-center justify-center h-full text-center text-[#C00000] font-semibold">
          {analysisError}
        </div>
      );
    }

    if (!analysisData) {
      return (
        <div className="flex items-center justify-center h-full text-center text-[#7A7A85]">
          Данные пока недоступны.
        </div>
      );
    }

    if (activeTab === 'diagnostics') {
      return (
        <div className="flex flex-col gap-4 pr-2">
          {analysisData.diagnostics.length ? (
            analysisData.diagnostics.map((paragraph, idx) => (
              <div key={`diagnostic-${idx}`} className="text-[26px] leading-relaxed font-normal">
                <ReactMarkdown
                  components={{
                    p: (paragraphProps) => <p className="mt-0 mb-2 last:mb-0" {...paragraphProps} />,
                  }}
                >
                  {paragraph}
                </ReactMarkdown>
              </div>
            ))
          ) : (
            <p className="text-sm text-[#7A7A85]">Нет данных для отображения.</p>
          )}
        </div>
      );
    }

    const targetTab: SliderTabKey = activeTab;
    const items = targetTab === 'techniques' ? analysisData.techniques : analysisData.hintQuestions;
    const currentIndex = sliderIndexes[targetTab] ?? 0;

    if (!items.length) {
      return <p className="text-sm text-[#7A7A85]">Нет данных для отображения.</p>;
    }

    const total = items.length;
    const canGoPrev = currentIndex > 0;
    const canGoNext = currentIndex < total - 1;

    const updateIndex = (idx: number) => {
      setSliderIndexes((prev) => ({ ...prev, [targetTab]: idx }));
    };

    return (
      <div className="flex flex-col h-full min-h-0">
        <div className="flex flex-col flex-1 min-h-0 gap-4 overflow-y-auto pr-2">
          <div
            className="text-[26px] leading-relaxed break-words min-w-0 pl-2"
            style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            <ReactMarkdown
              components={{
                p: (paragraphProps) => (
                  <p
                    className="mt-0 mb-2 last:mb-0 break-words"
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
                    {...paragraphProps}
                  />
                ),
                ol: (olProps) => (
                  <ol className="list-decimal pl-8" style={{ listStylePosition: 'outside' }} {...olProps} />
                ),
                li: (liProps) => (
                  <li
                    className="break-words"
                    style={{ wordBreak: 'break-word', overflowWrap: 'break-word', display: 'list-item' }}
                    {...liProps}
                  />
                ),
              }}
            >
              {items[currentIndex]}
            </ReactMarkdown>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-center gap-6 flex-shrink-0">
          <button
            type="button"
            onClick={() => canGoPrev && updateIndex(currentIndex - 1)}
            disabled={!canGoPrev}
            aria-label="Предыдущий пункт"
            className={classNames(
              'w-[30px] h-[30px] rounded-full flex items-center justify-center text-lg transition-colors cursor-pointer',
              canGoPrev
                ? 'bg-[#E6E6E6] text-[#BFBFBF] hover:bg-[#d4d4d4]'
                : 'bg-[#F3F3F3] text-[#C4C4C4] cursor-not-allowed',
            )}
          >
            ←
          </button>

          <div className="flex items-center gap-6">
            {items.map((_, idx) => {
              const isActive = idx === currentIndex;

              return (
                <button
                  key={`${targetTab}-dot-${idx}`}
                  type="button"
                  onClick={() => updateIndex(idx)}
                  aria-label={`Перейти к пункту ${idx + 1}`}
                  className={classNames(
                    'rounded-full transition-colors cursor-pointer',
                    isActive ? 'bg-[#9C9C9C]' : 'bg-[#E6E6E6] hover:bg-[#d4d4d4]',
                  )}
                  style={{ width: isActive ? 20 : 14, height: isActive ? 20 : 14 }}
                />
              );
            })}
          </div>

          <button
            type="button"
            onClick={() => canGoNext && updateIndex(currentIndex + 1)}
            disabled={!canGoNext}
            aria-label="Следующий пункт"
            className={classNames(
              'w-[30px] h-[30px] rounded-full flex items-center justify-center text-lg transition-colors cursor-pointer',
              canGoNext
                ? 'bg-[#C00000] text-white hover:bg-[#a00000]'
                : 'bg-[#E7B6B6] text-white/70 cursor-not-allowed',
            )}
          >
            →
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="flex h-full">
      {!(active && clientColors) && (
        <div className="w-[302px] min-w-[302px] h-full flex flex-col text-white rounded-l-[12px] font-comfortaa overflow-y-auto white-scroll p-6">
          <>
            <p className="text-2xl font-bold mb-4">Цветовой код</p>
            <div className="text-base font-manrope leading-relaxed">
              Предложение клиенту:
              <br />
              «Во время теста вам будут показаны карточки с различными цветами.
              <br />
              <br />
              Ваша задача –расставить карточки начиная с цвета, который вам нравится больше других в данный момент
              времени и далее выбирать самый предпочитаемый из оставшихся. <br />
              <br /> На последнем месте будет цвет, с которым вы чувствуете наименьшую симпатию по сравнению с
              остальными. <br />
              <br /> Не пытайтесь ассоциировать цвета ни с чем посторонним, – например, с цветом одежды или интерьера»
            </div>

            <ColorCards colors={COLORS} />

            <div className="mt-auto pt-6">
              <button
                className={classNames(
                  'w-full py-3 rounded-[10px] text-white font-bold text-base transition-colors',
                  active && !clientColors
                    ? 'bg-[#C00000]/50 text-white cursor-not-allowed'
                    : 'bg-[#C00000] text-white cursor-pointer hover:bg-[#a00000]',
                )}
                onClick={handleStartTest}
                disabled={active && !clientColors}
              >
                {active && !clientColors ? 'Клиент думает…' : 'Начать тест'}
              </button>
            </div>
          </>{' '}
        </div>
      )}

      {active && clientColors && (
        <div className="flex flex-col justify-between h-full w-[302px] min-w-[302px] text-white rounded-l-[12px] font-comfortaa p-6">
          <div>
            <h2 className="text-2xl font-bold mb-6">Цветовой код</h2>
            <p className="text-[18px] mb-8 font-manrope">Цветовой выбор Клиента</p>
            <ColorCards colors={clientColors} />
          </div>

          <div className="mt-6 flex flex-col gap-3 font-manrope font-normal">
            {ANALYSIS_TABS.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleTabClick(key)}
                className={classNames(
                  'w-full py-3 rounded-[10px] text-[16px] font-semibold transition-all cursor-pointer',
                  activeTab === key ? 'bg-[#DEC05E] text-[#2C3038]' : 'bg-[#828282] text-white hover:bg-[#9a9a9a]',
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      )}

      {activeTab && (
        <div className="flex-1 w-[616px] bg-white border-1 border-[#DEC05E] text-[#2C3038] rounded-[16px] p-8 leading-relaxed flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 flex flex-col min-h-0 overflow-y-auto">{renderAnalysisContent()}</div>
        </div>
      )}
    </div>
  );
};
